import type {Spec} from 'immutability-helper';
import React from 'react';

import {EntryProtocol} from 'notebook/ELNConstants';
import DocumentManagerComponent, {
  DocumentManagerRendererArgs,
} from 'notebook/mediocre/core-ui/DocumentManagerComponent';
import {SelectionRestoreData} from 'notebook/mediocre/selection/SelectionStore';
import ProtocolEditor from 'notebook/protocols/editor/Editor';
import HistoryManager from 'notebook/store/HistoryManager';
import {VisibilityOptions} from 'settings/visibility-options/VisibilityOptions';

interface ProtocolViewComponentProps {
  banner?: string;
  displayOptions: VisibilityOptions;
  fileId: string;
  historyManager: HistoryManager;
  locked: boolean;
  moreFormats: Array<{
    format: string;
    moreKey: string;
    onClick: (initialSelectorId: string) => void;
    tooltip: Array<string>;
  }>;
  onLoad?: () => void;
  onRetrying?: () => void;
  onSaved?: () => void;
  onSaving?: () => void;
  openManageSelectorsModal: () => void;
  openVersion: number;
  printHandler?: () => void;
  printLayout: boolean;
  printOptions?: Array<string>;
  protocol: EntryProtocol;
  revertProtocol: (protocol: EntryProtocol, selectionData: SelectionRestoreData) => void;
  savedVersion: number;
  saveSuccess: boolean;
  saving?: boolean;
  showPrintMargins: boolean;
  spellCheck: boolean;
  updateProtocol: (protocol: EntryProtocol, op: Spec<EntryProtocol>) => void;
}

export default class ProtocolViewComponent extends React.Component<ProtocolViewComponentProps> {
  override componentDidMount(): void {
    if (this.props.protocol != null) {
      if (typeof this.props.onLoad === 'function') {
        this.props.onLoad();
      }
    }
  }

  override componentDidUpdate(prevProps: ProtocolViewComponentProps): void {
    const {saving} = this.props;
    const unsaved = this.props.savedVersion !== this.props.openVersion;
    const noError = this.props.saveSuccess;
    if (saving || unsaved) {
      if (noError) {
        if (typeof this.props.onSaving === 'function') {
          this.props.onSaving();
        }
      } else if (typeof this.props.onRetrying === 'function') {
        this.props.onRetrying();
      }
    } else if (typeof this.props.onSaved === 'function') {
      this.props.onSaved();
    }

    if (this.props.protocol != null && prevProps.protocol == null) {
      if (typeof this.props.onLoad === 'function') {
        this.props.onLoad();
      }
    }
  }

  override render(): JSX.Element {
    if (this.props.protocol == null) {
      return <div>Loading...</div>;
    }
    return (
      <div className="protocol-view-component">
        <DocumentManagerComponent
          currentVersion={this.props.protocol}
          historyManager={this.props.historyManager}
          restoreStateAndSelection={this.props.revertProtocol}
          renderWith={({
            metadataForRestore,
            storeState,
            restoreState,
            requestSelection,
            undoStoreState,
          }: DocumentManagerRendererArgs) => (
            <ProtocolEditor
              banner={this.props.banner}
              fileId={this.props.fileId}
              protocol={this.props.protocol}
              printHandler={this.props.printHandler}
              showToolbar={true}
              locked={this.props.locked}
              moreFormats={this.props.moreFormats}
              printOptions={this.props.printOptions}
              updateProtocol={!this.props.locked ? this.props.updateProtocol : undefined}
              revertProtocol={!this.props.locked ? this.props.revertProtocol : undefined}
              openManageSelectorsModal={this.props.openManageSelectorsModal}
              displayOptions={this.props.displayOptions}
              spellCheck={this.props.spellCheck}
              printLayout={this.props.printLayout}
              showPrintMargins={this.props.showPrintMargins}
              // For selections
              metadataForRestore={metadataForRestore}
              storeState={storeState}
              undoStoreState={undoStoreState}
              restoreState={restoreState}
              requestSelection={requestSelection}
            />
          )}
        />
      </div>
    );
  }
}
